import { motion } from "framer-motion";

const services = [
  {
    id: "root-canal",
    n: "01",
    title: "Root Canal Treatment",
    span: "col-span-12 lg:col-span-8",
    img: "https://customer-assets.emergentagent.com/job_root-canal-experts-2/artifacts/clwb7l5g_IMG_0233.jpeg",
    body: "Single-visit endodontics under magnification. Painless, precise, sealed.",
    color: "#B67F4A",
  },
  {
    id: "cosmetic-laminates",
    n: "02",
    title: "Cosmetic Laminates",
    span: "col-span-12 lg:col-span-4",
    img: "https://images.unsplash.com/photo-1489278353717-f64c6ee8a4d2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxiZWF1dGlmdWwlMjBzbWlsZSUyMG1pbmltYWx8ZW58MHx8fHwxNzgzOTQ3NzU5fDA&ixlib=rb-4.1.0&q=85",
    body: "0.3 mm porcelain, shade-matched to the light of your face.",
    color: "#B49780",
  },
  {
    id: "dental-implants",
    n: "03",
    title: "Dental Implants",
    span: "col-span-12 lg:col-span-5",
    img: "https://customer-assets.emergentagent.com/job_root-canal-experts-2/artifacts/r7uou6gp_IMG_0235.jpeg",
    body: "Digitally-planned titanium implants with zirconia crowns.",
    color: "#7791AC",
  },
  {
    id: "orthodontics",
    n: "04",
    title: "Orthodontics & Aligners",
    span: "col-span-12 lg:col-span-7",
    img: "https://customer-assets.emergentagent.com/job_root-canal-experts-2/artifacts/2pmaofj2_IMG_0237.jpeg",
    body: "Clear aligners, ceramic brackets — the least invasive path to alignment.",
    color: "#94A9A6",
  },
  {
    id: "crowns-bridges",
    n: "05",
    title: "Crowns, Bridges & Zirconia",
    span: "col-span-12 lg:col-span-6",
    img: "https://images.unsplash.com/photo-1606811971618-4486d14f3f99?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwyfHxkZW50YWwlMjBjaGVja3VwfGVufDB8fHx8MTc4Mzk0Nzc2MHww&ixlib=rb-4.1.0&q=85",
    body: "Monolithic zirconia restorations designed to disappear.",
    color: "#8FA6BD",
  },
  {
    id: "extractions",
    n: "06",
    title: "Extractions & Impacted Teeth",
    span: "col-span-12 lg:col-span-6",
    img: "https://customer-assets.emergentagent.com/job_root-canal-experts-2/artifacts/75yfy0g2_IMG_0278.jpeg",
    body: "Atraumatic surgical extractions, including impacted third molars.",
    color: "#7A8B9F",
  },
];

function ServiceCard({ s, i }) {
  return (
    <motion.a
      href="tel:+919961717181"
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.9, delay: (i % 3) * 0.08, ease: [0.76, 0, 0.24, 1] }}
      className={`group relative block ${s.span} border-t border-bone pt-8`}
      data-testid={`service-card-${s.id}`}
    >
      <div className="flex items-baseline justify-between mb-8">
        <span className="eyebrow">{s.n}</span>
        <span className="eyebrow text-ink/60 group-hover:text-ink transition-colors duration-500">
          Discover →
        </span>
      </div>

      <div className="relative aspect-[16/10] w-full overflow-hidden mb-8">
        <img
          src={s.img}
          alt={s.title}
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1600ms] ease-out group-hover:scale-[1.06]"
        />
        <div className="absolute inset-0 bg-ink/10 group-hover:bg-ink/0 transition-colors duration-700" />
      </div>

      <h3 className="font-serif font-light text-2xl md:text-4xl leading-[1.05] tracking-tight max-w-[18ch]" style={{ color: s.color }}>
        {s.title}
      </h3>
      <p className="mt-4 text-ink/70 text-sm md:text-base leading-relaxed max-w-[40ch] font-light">
        {s.body}
      </p>
    </motion.a>
  );
}

export default function Services() {
  return (
    <section
      id="treatments"
      data-testid="services-section"
      className="relative py-28 md:py-40 bg-porcelain border-b border-bone"
    >
      <div className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-12 gap-6 md:gap-10 mb-20 md:mb-28">
          <div className="col-span-12 md:col-span-4">
            <div className="flex items-center gap-4 mb-8">
              <span className="w-10 h-px bg-ink" />
              <span className="eyebrow">Treatments</span>
            </div>
            <p className="text-ink/70 text-sm max-w-[32ch] font-light leading-relaxed">
              A focused practice. Six disciplines, one philosophy — the least invasive
              route to a healthier, quieter smile.
            </p>
          </div>
          <div className="col-span-12 md:col-span-8">
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1] }}
              className="font-serif font-light text-4xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight"
              data-testid="services-heading"
              style={{ color: "#4A3520" }}
            >
              Every discipline,
              <br />
              <span className="italic" style={{ color: "#1E5AA8" }}>practiced quietly.</span>
            </motion.h2>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-6 md:gap-10">
          {services.map((s, i) => (
            <ServiceCard key={s.id} s={s} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
